import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError } from '../config/api';
import { usePublishVideo, useVideoRun } from '../hooks/use-video-runs';

/**
 * Single video run — full-size preview, caption, asset metadata, and the
 * Publish-to-Instagram action (Phase 4 endpoint).
 */
export function VideoRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const runId = id ?? null;
  const { data, isLoading, error, refetch } = useVideoRun(runId);
  const publish = usePublishVideo(runId);
  const [captionOverride, setCaptionOverride] = useState('');

  if (isLoading) return <div className="muted page-pad">Loading…</div>;
  if (error)
    return (
      <div className="error page-pad">
        {error instanceof Error ? error.message : String(error)}
      </div>
    );
  if (!data) return <div className="muted page-pad">Not found.</div>;

  const { run, assets, posts } = data;
  const videoAsset = assets.find((a) => a.kind === 'video');
  const thumbAsset = assets.find((a) => a.kind === 'thumbnail');

  // The run is publishable once its assets have rendered. A run that failed at
  // the publish step still has its video in storage, so it can be retried
  // manually — `rendered_at` is the authoritative "assets exist" signal. Only a
  // run that never finished rendering is gated out.
  const canPublish =
    run.status === 'rendered' ||
    run.status === 'published' ||
    run.rendered_at !== null;

  function onPublish() {
    publish.mutate({
      platforms: ['instagram'],
      captionOverride: captionOverride.trim() || undefined,
    });
  }

  return (
    <div className="run-detail">
      <header className="run-detail-head">
        <div>
          <div className="panel-label">Video run</div>
          <span className="mono">{run.id}</span>
        </div>
        <div className={`status status-${run.status} status-pill`}>
          {run.status}
        </div>
      </header>

      <div className="run-detail-grid">
        <section className="panel video-result">
          {videoAsset ? (
            <video
              className="video-player"
              src={videoAsset.gcs_url}
              poster={thumbAsset?.gcs_url}
              controls
              playsInline
            />
          ) : (
            <div className="dim">No video asset on this run.</div>
          )}
        </section>

        <section className="panel">
          <div className="panel-label">Metadata</div>
          <dl className="kv">
            <dt>Template</dt>
            <dd className="mono small">{run.template_id}</dd>
            <dt>Article</dt>
            <dd className="mono small">{run.article_id ?? '—'}</dd>
            <dt>Test user</dt>
            <dd>{run.test_user_email}</dd>
            <dt>Country</dt>
            <dd>{run.country ?? 'global'}</dd>
            <dt>Category</dt>
            <dd>{run.category ?? '—'}</dd>
            <dt>Trigger</dt>
            <dd>{run.trigger}</dd>
            <dt>Created</dt>
            <dd>{new Date(run.created_at).toLocaleString()}</dd>
            <dt>Rendered</dt>
            <dd>
              {run.rendered_at
                ? new Date(run.rendered_at).toLocaleString()
                : '—'}
            </dd>
            <dt>Published</dt>
            <dd>
              {run.published_at
                ? new Date(run.published_at).toLocaleString()
                : '—'}
            </dd>
          </dl>
          {run.error ? (
            <div className="error" style={{ marginTop: 8 }}>
              {run.error}
            </div>
          ) : null}
        </section>

        <section className="panel">
          <div className="panel-label">Caption</div>
          <pre className="caption-text">{run.caption ?? '(none)'}</pre>
        </section>

        <section className="panel">
          <div className="panel-label">Assets</div>
          <ul className="asset-list">
            {assets.map((a) => (
              <li key={a.id}>
                <a href={a.gcs_url} target="_blank" rel="noreferrer">
                  {a.kind}
                </a>{' '}
                <span className="dim small">
                  {a.width}×{a.height} ·{' '}
                  {Math.round(a.file_size_bytes / 1024)} KB
                  {a.duration_ms
                    ? ` · ${(a.duration_ms / 1000).toFixed(1)}s`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="panel publish-panel">
          <div className="panel-label">Publish to Instagram Reels</div>
          {!canPublish ? (
            <div className="dim small">
              Publish becomes available once the run reaches{' '}
              <span className="status status-rendered">rendered</span>.
            </div>
          ) : (
            <>
              <label>
                Caption override (optional)
                <textarea
                  className="input"
                  rows={4}
                  value={captionOverride}
                  onChange={(e) => setCaptionOverride(e.target.value)}
                  placeholder={run.caption ?? ''}
                />
              </label>
              <button
                className="btn btn-primary"
                disabled={publish.isPending}
                onClick={onPublish}
              >
                {publish.isPending
                  ? 'Publishing…'
                  : run.status === 'published'
                    ? 'Re-publish'
                    : 'Publish'}
              </button>
              {publish.error ? (
                <div className="error" style={{ marginTop: 8 }}>
                  {publish.error instanceof ApiError
                    ? `${publish.error.message} — ${JSON.stringify(
                        publish.error.body,
                      ).slice(0, 240)}`
                    : (publish.error as Error).message}
                </div>
              ) : null}
            </>
          )}

          {posts && posts.length > 0 ? (
            <ul className="post-list" style={{ marginTop: 12 }}>
              {posts.map((p) => (
                <li key={p.id}>
                  <span className={`status status-${p.status}`}>{p.status}</span>{' '}
                  {p.platform}
                  {p.post_url ? (
                    <>
                      {' '}
                      ·{' '}
                      <a href={p.post_url} target="_blank" rel="noreferrer">
                        view
                      </a>
                    </>
                  ) : null}
                  {p.error ? (
                    <div className="dim small">{p.error}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </div>

      <div style={{ marginTop: 24 }}>
        <Link to="/video" className="btn-link">
          ← Back to video composer
        </Link>{' '}
        ·{' '}
        <button className="btn-link" onClick={() => void refetch()}>
          Refresh
        </button>
      </div>
    </div>
  );
}
